import { Controller } from "@hotwired/stimulus";

/**
 * The summary beside the one-page fee form (30/10/2026): the name, the dues,
 * the tariffs and the activities follow what is typed — names, amounts, due
 * dates, lines added or removed — without reloading the page. The type and the
 * classes already reload it (form-refresh). A fee by plans keeps the summary
 * the page was loaded with: its rates are read on the plan cards.
 *
 * <form data-controller="fee-summary"
 *       data-action="input->fee-summary#update change->fee-summary#update click->fee-summary#defer"
 *       data-fee-summary-variant-value="installments"
 *       data-fee-summary-total-label-value="Montant total"
 *       data-fee-summary-mandatory-label-value="Obligatoire">
 *   <div id="fee_amounts">… the nested cards …</div>
 *   <dd data-fee-summary-target="name">…</dd>
 *   <div data-fee-summary-target="amounts">…</div>
 */
const INSTALLMENT_VARIANTS = ["installments", "registration_installments", "single_installment", "exam_installments"];
const LIVE_VARIANTS = [...INSTALLMENT_VARIANTS, "tariffs", "activities"];

export default class extends Controller {
  static targets = ["name", "amounts"];
  static values = {
    variant: String,
    totalLabel: String,
    mandatoryLabel: String,
    currency: { type: String, default: "FCFA" },
    empty: { type: String, default: "—" },
  };

  update() {
    if (this.hasNameTarget) this.nameTarget.textContent = this.field(this.element, "fee_setting[name]") || this.emptyValue;
    if (!this.hasAmountsTarget || !LIVE_VARIANTS.includes(this.variantValue)) return;

    const rows = this.lines();
    const nodes = rows.map((line) => this.row(line));
    if (INSTALLMENT_VARIANTS.includes(this.variantValue)) {
      nodes.push(this.total(rows.reduce((sum, line) => sum + (line.amount || 0), 0)));
    }
    this.amountsTarget.replaceChildren(...nodes);
  }

  // Adding or removing a card is a click handled by the cards' own controller:
  // the summary reads the cards once it has run.
  defer() {
    requestAnimationFrame(() => this.update());
  }

  // The visible cards of the « Échéances et montants » section.
  lines() {
    const section = this.element.querySelector("#fee_amounts");
    if (!section) return [];

    return [...section.querySelectorAll("[data-fee-nested-fields-target='fields']")]
      .filter((card) => card.style.display !== "none")
      .map((card) => ({
        name: this.field(card, "[name]", true),
        date: this.field(card, "[due_date]", true) || this.field(card, "[event_date]", true),
        amount: parseFloat(this.field(card, "[amount]", true) || this.field(card, "[unit_price]", true)) || 0,
        mandatory: this.variantValue === "activities" && card.querySelector('input[type="checkbox"][name$="[mandatory]"]')?.checked,
      }));
  }

  row(line) {
    const row = this.node("div", "ds-filerow");
    const body = this.node("div", "ds-recentrow__body");
    const title = this.node("div", "ds-recentrow__title", line.name || this.emptyValue);
    if (line.mandatory && this.hasMandatoryLabelValue) title.append(" ", this.node("span", "ds-section-meta", this.mandatoryLabelValue));
    body.append(title);
    if (line.date) body.append(this.node("div", "ds-recentrow__detail", this.shortDate(line.date)));

    const amount = this.node("span", "tabular-nums", this.money(line.amount));
    amount.style.cssText = "font-weight:600; color:var(--ds-text-1);";
    row.append(body, amount);
    return row;
  }

  total(sum) {
    const row = this.node("div", "flex items-center justify-between pt-2");
    row.style.borderTop = "1px solid var(--ds-border)";
    const value = this.node("span", "tabular-nums", this.money(sum));
    value.style.cssText = "font-weight:700; color:var(--ds-accent);";
    row.append(this.node("span", "ds-section-meta", this.totalLabelValue), value);
    return row;
  }

  node(tag, className, text) {
    const element = document.createElement(tag);
    element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  // A field of the form by its full name, or of a card by the end of its name
  // (a date picked with flatpickr keeps its value in a hidden field).
  field(root, name, suffix = false) {
    const selector = suffix ? `[name$="${name}"]:not([type="checkbox"])` : `[name="${name}"]`;
    return root.querySelector(selector)?.value?.trim() || "";
  }

  // As the page draws them: « 150 000 FCFA ».
  money(amount) {
    const digits = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${digits} ${this.currencyValue}`;
  }

  shortDate(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) return iso;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return new Intl.DateTimeFormat(document.documentElement.lang || "fr", { day: "numeric", month: "short" }).format(date);
  }
}
